import React, { useState, useEffect, useMemo } from 'react'
import ReactTooltip from 'react-tooltip'
import { useSelector, useDispatch } from 'react-redux'
import {
  PLAN_STATUS,
  MY_SERVICES,
  TV_PLAN_CARDS,
  TV_PLAN_EVENTS,
  FIREBASE_EVENTS,
  EVENT_STATUS,
} from 'app-constants'
import { Card, Button, Alert } from 'components'
import { STATICS } from 'statics'
import { fetchContractDetails, fetchTvPlansActivationLink } from 'slices/contract'
import { useFirebaseEvent } from 'hooks'
import { contractService } from 'services'
import { openNewTab, getTvButtonInfo } from 'utils'
import { Pending, RecurringPayment } from './components'
import {
  DOWNLOAD_CONTRACT_ERROR_MESSAGE,
  DOWNLOAD_CONTRACT_ERROR_TITLE,
  HIDE_TV_PASSWORD_TOOLTIP_TEXT,
  SHOW_TV_PASSWORD_TOOLTIP_TEXT,
} from './constants'
import './style.scss'

export function PlanDetails() {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const [isLoadingDownloadButton, setIsLoadingDownloadButton] = useState(false)
  const [showTvPassword, setShowTvPassword] = useState(false)
  const { selected: contract, detail: contractDetails, activationLinks } = useSelector(
    state => state.contract
  )
  const { getContractFile } = contractService()
  const { sendEvent } = useFirebaseEvent()
  const dispatch = useDispatch()

  const title = useMemo(() => {
    if (pending)
      return (
        <h3 className='title'>
          Acompanhe o status <br />
          do seu pedido
        </h3>
      )

    return (
      <h3 className='title'>
        Detalhamento <br />
        do meu plano
      </h3>
    )
  }, [pending])

  useEffect(() => {
    const isPendentContract = contractDetails?.status === PLAN_STATUS.ST_CONT_EM_ATIVACAO

    if (!contractDetails) dispatch(fetchContractDetails(contract.id))
    if (isPendentContract) setPending(true)
    dispatch(fetchTvPlansActivationLink())
  }, [dispatch, contractDetails, contract.id])

  async function handleDownloadContract() {
    try {
      setIsLoadingDownloadButton(true)
      const { data } = await getContractFile(contract.id)
      openNewTab(data)
      sendEvent(FIREBASE_EVENTS.CLICK_ON_DOWNLOAD_CONTRACT_PDF)
    } catch (err) {
      sendEvent(FIREBASE_EVENTS.CLICK_ON_DOWNLOAD_CONTRACT_PDF, EVENT_STATUS.ERROR)
      console.error(err)
      setError({
        title: DOWNLOAD_CONTRACT_ERROR_TITLE,
        text: DOWNLOAD_CONTRACT_ERROR_MESSAGE,
      })
    } finally {
      setIsLoadingDownloadButton(false)
    }
  }

  function handleAlertClose() {
    setError(null)
  }

  function handleClickShowTvPassword() {
    setShowTvPassword(!showTvPassword)
  }

  function getIsTvPlanCardAdditional(itemName) {
    return Object.values(TV_PLAN_CARDS).includes(itemName)
  }

  function getTvPlanActivationLink(itemCode) {
    const activationLink = activationLinks.find(({ tvPlanKey }) => tvPlanKey === itemCode)

    return activationLink?.activationLink || ''
  }

  function getEventLabel(isTvPlanAdditional, itemCode) {
    const event = isTvPlanAdditional ? TV_PLAN_EVENTS[itemCode] : MY_SERVICES[itemCode]?.eventName

    return event || itemCode
  }

  function handleSendEvent(isTvPlanAdditional, itemCode) {
    const event = getEventLabel(isTvPlanAdditional, itemCode)

    sendEvent(event)
  }

  function renderActionButton(itemCode, activeSva, urlDocument, status) {
    const isTvPlanAdditional = getIsTvPlanCardAdditional(itemCode)
    const activationLink = isTvPlanAdditional ? getTvPlanActivationLink(itemCode) : null
    const buttonInfo = getTvButtonInfo({ activeSva, urlDocument, status }, activationLink)

    if (!buttonInfo) return null

    const { label, link } = buttonInfo

    return (
      <a href={link} rel='noreferrer' target='_blank' className='see-details'>
        <Button
          color='primary'
          outlined
          className='see-details'
          onClick={() => handleSendEvent(isTvPlanAdditional, itemCode)}
          data-cy='button-see-details'
        >
          {label}
        </Button>
      </a>
    )
  }

  function renderAdditionalSvaLogo(urlLogo, urlImage, itemName) {
    const urlSvaLogo = urlLogo || urlImage

    if (!urlSvaLogo) return null

    return <img src={urlSvaLogo} alt={itemName} className='img' />
  }

  function renderAdditionals() {
    const hasContractAdditionals = contractDetails.contractAdditionals.length

    if (!hasContractAdditionals) return null

    return (
      <>
        <p className='subtitle'>Serviços adicionais</p>
        {contractDetails.contractAdditionals.map(
          ({ itemCode, urlDocument, urlLogo, urlImage, itemName, activeSva, status }) => (
            <Card className='card -additional -flex' key={itemCode}>
              <div className='container-info'>
                {renderAdditionalSvaLogo(urlLogo, urlImage, itemName)}
                <p className='text'>{itemName}</p>
              </div>
              <div className='container-buttons'>
                {renderActionButton(itemCode, activeSva, urlDocument, status)}
              </div>
            </Card>
          )
        )}
      </>
    )
  }

  function renderShowTvPasswordButton() {
    return (
      <>
        <div
          className='icon'
          data-for='tv-password-tip'
          data-tip={showTvPassword ? HIDE_TV_PASSWORD_TOOLTIP_TEXT : SHOW_TV_PASSWORD_TOOLTIP_TEXT}
        >
          {showTvPassword ? (
            <STATICS.EYE onClick={handleClickShowTvPassword} />
          ) : (
            <STATICS.EYE_SLASH onClick={handleClickShowTvPassword} />
          )}
        </div>
        <ReactTooltip id='tv-password-tip' />
      </>
    )
  }

  function renderDownloadButton() {
    return (
      <Button
        color='primary'
        className='download-button'
        outlined
        onClick={handleDownloadContract}
        isLoading={isLoadingDownloadButton}
      >
        Download do PDF
      </Button>
    )
  }

  function renderAdditionalTvPoints() {
    const hasAdditionalTvPoints = contractDetails.additionalTvPointsQuantity > 0

    if (!hasAdditionalTvPoints) return null

    return (
      <p className='text'>
        {contractDetails.additionalTvPointsQuantity} Ponto opcional (HD):{' '}
        {contractDetails.formattedAdditionalTvPointsTotalPrice}
      </p>
    )
  }

  function renderPending() {
    if (!pending) return null

    return <Pending />
  }

  function renderTvLogin() {
    const { tvLogin, tvPassword } = contractDetails
    const hasTvPassword = !!contractDetails.tvPassword

    if (!hasTvPassword) return null

    return (
      <>
        <p className='subtitle'>Login da TV | Sumicity Play</p>
        <Card className='card'>
          <p className='text'>Usuário: {tvLogin}</p>
          <div className='tv-password-container'>
            <p className='text'>Senha :&nbsp;</p>
            <p className={showTvPassword ? 'password' : 'password -hidden'}>{tvPassword}</p>
            {renderShowTvPasswordButton()}
          </div>
        </Card>
      </>
    )
  }

  function renderError() {
    if (!error) return null

    return (
      <Alert
        className='alert'
        severity='error'
        size='large'
        title={error.title}
        text={error.text}
        onClose={handleAlertClose}
      />
    )
  }

  function renderComponentContent() {
    if (!contractDetails) return null

    const { planName, formattedTotalPrice, paymentMethod } = contractDetails

    return (
      <div className='plan-details-component'>
        {title}
        {renderError()}
        {renderPending()}
        <p className='subtitle'>Plano contratado</p>
        <Card className='card'>
          <p className='text'>{planName}</p>
          <p className='text'>Valor: {formattedTotalPrice}</p>
          {renderAdditionalTvPoints()}
          <p className='text'>Status: {PLAN_STATUS[contract.contractStatus]}</p>
          <p className='text'>Forma de pagamento: {paymentMethod}</p>
          <RecurringPayment
            contractId={contract.id}
            contract={contractDetails}
            setError={setError}
          />
        </Card>
        {renderTvLogin()}
        {renderAdditionals()}
        <Card className='card -contract -flex'>
          <div className='content'>
            <p className='title'>Meu contrato</p>
            <p className='text'>{planName}</p>
          </div>
          {!pending && renderDownloadButton()}
        </Card>
      </div>
    )
  }

  return renderComponentContent()
}
